/**
 * FieldSection — collapsible card wrapper for a group of content fields.
 * Remembers its open/closed state across re-renders via localStorage key.
 */

import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

interface FieldSectionProps {
  title:        string;
  icon?:        React.ReactNode;
  /** Unique key used to persist collapsed state in localStorage */
  storageKey?:  string;
  defaultOpen?: boolean;
  /** Show a dot badge when the section has content */
  hasContent?:  boolean;
  children:     React.ReactNode;
}

export default function FieldSection({
  title,
  icon,
  storageKey,
  defaultOpen = true,
  hasContent  = false,
  children,
}: FieldSectionProps) {
  const [open, setOpen] = useState(() => {
    if (storageKey) {
      const stored = localStorage.getItem(`section-${storageKey}`);
      if (stored !== null) return stored === 'true';
    }
    return defaultOpen;
  });

  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(`section-${storageKey}`, String(open));
    }
  }, [open, storageKey]);

  return (
    <div className="rounded-xl border border-surface-200 bg-white overflow-hidden shadow-sm">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-surface-50 transition-colors"
      >
        {open
          ? <ChevronDown className="h-4 w-4 text-surface-400 shrink-0" />
          : <ChevronRight className="h-4 w-4 text-surface-400 shrink-0" />
        }

        {icon && <span className="text-surface-500 shrink-0">{icon}</span>}

        <span className="text-sm font-semibold text-surface-900 flex-1">{title}</span>

        {hasContent && !open && (
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 shrink-0" title="Has content" />
        )}
      </button>

      {/* Content */}
      {open && (
        <div className={cn('px-5 pb-5 space-y-4 border-t border-surface-100')}>
          <div className="pt-4">{children}</div>
        </div>
      )}
    </div>
  );
}
